import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useIntl } from 'react-intl';

import { Icon } from '@verantos-dev/shared-components';

import type { ReassignmentRowState } from './types';
import { useReassignmentRow } from './useReassignmentRow';
import type { AnnotatorBatchBreakdown, ReallocationBatchKey } from '../../types/response';

interface ReassignmentRowProps {
  projectId: string;
  batch: ReallocationBatchKey;
  row: ReassignmentRowState;
  rows: ReassignmentRowState[];
  perAnnotator: AnnotatorBatchBreakdown[];
  onChange: (patch: Partial<ReassignmentRowState>) => void;
  onRemove: () => void;
}

export const ReassignmentRow = ({
  projectId,
  batch,
  row,
  rows,
  perAnnotator,
  onChange,
  onRemove,
}: ReassignmentRowProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();

  const {
    fromOptions,
    toOptions,
    bothSelected,
    effectivePending,
    isFetchingMovable,
    annotatorName,
    hasError,
  } = useReassignmentRow({
    projectId,
    batch,
    row,
    rows,
    perAnnotator,
    onChange,
  });

  const renderHelperText = () => {
    if (!row.fromAnnotatorId) return null;

    // The movable cap for this pair is in flight — placeholder the message line
    // so it doesn't flash stale text before the fetched cap lands.
    if (isFetchingMovable) {
      return (
        <Skeleton variant="text" width={320} sx={{ fontSize: theme.typography.caption.fontSize }} />
      );
    }

    // For a target with a known movable cap, show the pairwise message —
    // shown for both saved and newly-added (unsaved) "to" targets.
    if (bothSelected && row.movable != null) {
      return (
        <Typography
          variant="caption"
          sx={{ color: hasError ? theme.palette.error.main : theme.palette.warning.dark }}
        >
          {formatMessage(
            {
              id:
                row.movable === 0
                  ? 'adminProjects.reallocation.row.movableInfoNone'
                  : 'adminProjects.reallocation.row.movableInfo',
            },
            {
              movable: row.movable,
              fromName: annotatorName(row.fromAnnotatorId),
              pending: effectivePending,
              toName: annotatorName(row.toAnnotatorId),
            }
          )}
        </Typography>
      );
    }

    return (
      <Typography
        variant="caption"
        sx={{ color: hasError ? theme.palette.error.main : theme.palette.text.secondary }}
      >
        {formatMessage(
          { id: 'adminProjects.reallocation.row.maxPending' },
          { count: effectivePending }
        )}
      </Typography>
    );
  };

  const placeholder = formatMessage({ id: 'adminProjects.reallocation.row.selectAnnotator' });

  // Clear affordance for the From/To selects so a chosen annotator can be reset
  // back to the placeholder state. mr on the button (plus the matching Select
  // paddingRight below) keeps it clear of the dropdown's own arrow icon.
  const clearAdornment = (value: string, id: string, ariaLabelId: string, onClear: () => void) =>
    value ? (
      <IconButton
        size="small"
        aria-label={formatMessage({ id: ariaLabelId })}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onClear();
        }}
        sx={{ mr: theme.spacing(4) }}
      >
        <Icon icon="clear" id={id} width={14} height={14} />
      </IconButton>
    ) : undefined;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(1) }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: theme.spacing(2) }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {formatMessage({ id: 'adminProjects.reallocation.row.fromAnnotator' })}
          </Typography>
          <Select
            fullWidth
            size="small"
            displayEmpty
            value={row.fromAnnotatorId}
            onChange={(e) => onChange({ fromAnnotatorId: e.target.value })}
            inputProps={{
              'aria-label': formatMessage({ id: 'adminProjects.reallocation.row.fromAnnotator' }),
            }}
            endAdornment={clearAdornment(
              row.fromAnnotatorId,
              'reassignment-row-clear-from-icon',
              'adminProjects.reallocation.row.clearFromAnnotator',
              () => onChange({ fromAnnotatorId: '' })
            )}
            sx={{ '& .MuiSelect-select': { pr: '64px !important' } }}
            renderValue={(value) =>
              value ? (
                annotatorName(value)
              ) : (
                <Box sx={{ color: theme.palette.text.disabled }}>{placeholder}</Box>
              )
            }
          >
            {fromOptions.length === 0 ? (
              <MenuItem disabled>
                {formatMessage({ id: 'adminProjects.reallocation.row.noAnnotators' })}
              </MenuItem>
            ) : (
              fromOptions.map((a) => (
                <MenuItem key={a.annotatorId} value={a.annotatorId}>
                  {a.annotatorName}
                </MenuItem>
              ))
            )}
          </Select>
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {formatMessage({ id: 'adminProjects.reallocation.row.toAnnotator' })}
          </Typography>
          <Select
            fullWidth
            size="small"
            displayEmpty
            value={row.toAnnotatorId}
            onChange={(e) => onChange({ toAnnotatorId: e.target.value })}
            inputProps={{
              'aria-label': formatMessage({ id: 'adminProjects.reallocation.row.toAnnotator' }),
            }}
            endAdornment={clearAdornment(
              row.toAnnotatorId,
              'reassignment-row-clear-to-icon',
              'adminProjects.reallocation.row.clearToAnnotator',
              () => onChange({ toAnnotatorId: '' })
            )}
            sx={{ '& .MuiSelect-select': { pr: '64px !important' } }}
            renderValue={(value) =>
              value ? (
                annotatorName(value)
              ) : (
                <Box sx={{ color: theme.palette.text.disabled }}>{placeholder}</Box>
              )
            }
          >
            {toOptions.length === 0 ? (
              <MenuItem disabled>
                {formatMessage({ id: 'adminProjects.reallocation.row.noAnnotators' })}
              </MenuItem>
            ) : (
              toOptions.map((a) => (
                <MenuItem key={a.annotatorId} value={a.annotatorId}>
                  {a.annotatorName}
                </MenuItem>
              ))
            )}
          </Select>
        </Box>

        <Box sx={{ width: 180 }}>
          <Typography variant="caption" color="text.secondary">
            {formatMessage({ id: 'adminProjects.reallocation.row.numberOfEncounters' })}
          </Typography>
          <TextField
            type="number"
            size="small"
            fullWidth
            error={hasError}
            value={row.count ?? ''}
            onChange={(e) => {
              const raw = e.target.value;
              onChange({ count: raw === '' ? null : Math.trunc(Number(raw)) });
            }}
            inputProps={{
              min: 0,
              'aria-label': formatMessage({
                id: 'adminProjects.reallocation.row.numberOfEncounters',
              }),
            }}
          />
        </Box>

        <IconButton
          aria-label="remove reassignment"
          onClick={onRemove}
          disabled={rows.length === 1}
          sx={{ mb: theme.spacing(1) }}
        >
          <DeleteOutlineIcon />
        </IconButton>
      </Box>
      {renderHelperText()}
    </Box>
  );
};
